import * as Blockly from 'blockly/core';
import { defineBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';

const SIZE_MAX_COLS: Record<string, number> = {
    '1': 16, // 128 / 8px
    '2': 8, // 128 / 16px
    '3': 5, // 128 / 24px
};

function filterAllowed(str: string): string {
    return str.replace(/[\u4E00-\u9FFF\u3400-\u4DBF]/gu, '');
}

function getMaxCols(block: Blockly.Block): number {
    const parent = block.getSurroundParent();
    const size = parent?.getFieldValue('TEXT_SIZE') ?? '1';
    return SIZE_MAX_COLS[size] ?? 16;
}

function truncate(str: string, block: Blockly.Block): string {
    const max = getMaxCols(block);
    return Array.from(str).slice(0, max).join('');
}

class OledStringField extends Blockly.FieldTextInput {
    constructor(value: string) {
        super(value);
        this.SERIALIZABLE = true;
    }

    override initView() {
        super.initView();
        const t = (this as any).textElement_ as SVGTextElement | undefined;
        t?.classList.add('blocklyStringFieldText');
    }

    override getText(): string {
        const v = this.getValue() ?? '';
        if (v === '') return '" "';
        return `"${v}"`;
    }

    protected override render_() {
        super.render_();
        const t = (this as any).textElement_ as SVGTextElement | undefined;
        t?.classList.add('blocklyStringFieldText');

        const block = this.getSourceBlock() as Blockly.BlockSvg | null;
        if (!block) return;
        const ws = block.workspace as Blockly.WorkspaceSvg;
        if (ws?.isFlyout) {
            requestAnimationFrame(() => {
                (this as any).updateSize_?.();
                block.render();
            });
        }
    }

    override showEditor_(): void {
        super.showEditor_();
        const input = document.querySelector('.blocklyHtmlInput') as HTMLInputElement | null;
        if (!input) return;

        input.classList.add('blocklyStringInput');
        input.value = this.getValue() ?? '';

        const block = this.getSourceBlock();
        const maxCols = block ? getMaxCols(block) : 16;
        input.maxLength = maxCols;

        const widgetDiv = input.closest('.blocklyWidgetDiv') as HTMLElement | null;
        if (widgetDiv) {
            widgetDiv.style.borderRadius = '4px';
            input.style.borderRadius = '4px';

            const fieldRect = (this as any).textElement_?.getBoundingClientRect();
            const canvasTop =
                (this.getSourceBlock() as Blockly.BlockSvg)?.workspace?.getParentSvg()?.getBoundingClientRect()?.top ??
                0;
            if (fieldRect) {
                widgetDiv.style.top = `${fieldRect.top - canvasTop}px`;
            }
        }

        // 漢字入力をリアルタイムで弾く
        const onInput = () => {
            const filtered = filterAllowed(input.value);
            if (filtered !== input.value) {
                const pos = input.selectionStart ?? filtered.length;
                input.value = filtered;
                input.setSelectionRange(pos, pos);
            }
        };
        input.addEventListener('input', onInput);
    }

    override doValueUpdate_(newValue: string) {
        const block = this.getSourceBlock();
        const filtered = filterAllowed(newValue);
        const truncated = block ? truncate(filtered, block) : filtered;
        super.doValueUpdate_(truncated);
    }
}

defineBlock({
    type: 'string_oled',
    colour: CATEGORIES.display.color,
    output: 'String',
    outputShape: 3,
    inputs: [
        {
            kind: 'dummy',
            fields: [
                {
                    type: 'field',
                    name: 'TEXT',
                    instance: () => new OledStringField(''),
                },
            ],
        },
    ],
});
